import { useState, useEffect, useRef, useCallback } from 'react';
import tokenManager from '../utils/tokenManager';

export interface SearchQuery {
  query_id: string;
  text: string;
  attempt: number;
  timestamp: string;
}

export interface SearchResult {
  query_id: string;
  rank: number;
  url: string;
  title: string;
  snippet: string;
  timestamp: string;
}

export interface SearchProgressState {
  isActive: boolean;
  status: 'starting' | 'searching' | 'completed' | 'error' | 'cancelled';
  provider?: string;
  queries: SearchQuery[];
  results: SearchResult[];
  totalResults?: number;
  duration?: number;
  error?: string;
}

export interface SearchEventData {
  event: string;
  event_id: string;
  message_id: number;
  timestamp: string;
  data: any;
}

const initialState: SearchProgressState = {
  isActive: false,
  status: 'starting',
  queries: [],
  results: [],
};

export const useSearchProgress = (chatId: number | null, messageId: number | null) => {
  console.log('useSearchProgress: Hook initialized', { chatId, messageId });
  const [searchProgress, setSearchProgress] = useState<SearchProgressState>(initialState);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connectEventSource = useCallback(() => {
    if (!chatId) {
      console.log('useSearchProgress: chatId is null, not connecting');
      return;
    }
    
    console.log('useSearchProgress: Connecting EventSource', { chatId, messageId });

    // 既存の接続をクリーンアップ
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // tokenManagerを使用してトークンを取得
      const token = tokenManager.getToken();
      if (!token) {
        console.warn('認証トークンがありません');
        return;
      }
      console.log('SearchProgress: Token found', { tokenLength: token.length });

      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/chats/${chatId}/search-progress?token=${encodeURIComponent(token)}`;
      console.log('SearchProgress: SSE URL', url);
      const eventSource = new EventSource(url);

      eventSource.onopen = () => {
        console.log('SearchProgress: SSE接続開始', { chatId });
        reconnectAttempts.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleSearchEvent(data);
        } catch (error) {
          console.error('SearchProgress: イベントデータ解析エラー', error, event.data);
        }
      };

      eventSource.addEventListener('search.progress', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data);
          handleSearchEvent(data);
        } catch (error) {
          console.error('SearchProgress: search.progressイベント解析エラー', error);
        }
      });

      eventSource.addEventListener('heartbeat', (event) => {
        console.debug('SearchProgress: ハートビート受信');
      });

      eventSource.onerror = (error) => {
        console.error('SearchProgress: SSE接続エラー', error);
        eventSource.close();
        
        // 自動再接続（指数バックオフ）
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          
          console.log(`SearchProgress: ${backoffDelay}ms後に再接続試行 (${reconnectAttempts.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectEventSource();
          }, backoffDelay);
        } else {
          console.error('SearchProgress: 最大再接続回数に到達、接続を停止します');
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('SearchProgress: EventSource作成エラー', error);
    }
  }, [chatId, messageId]);

  const handleSearchEvent = useCallback((eventData: SearchEventData) => {
    console.log('SearchProgress: イベント受信', eventData);

    // メッセージIDが一致しない場合はスキップ
    if (messageId && eventData.message_id !== messageId) {
      return;
    }

    setSearchProgress(prevState => {
      const newState = { ...prevState };

      switch (eventData.event) {
        case 'search_start':
          newState.isActive = true;
          newState.status = 'starting';
          newState.provider = eventData.data.provider;
          newState.queries = [];
          newState.results = [];
          newState.error = undefined;
          break;

        case 'search_query':
          newState.status = 'searching';
          const query: SearchQuery = {
            query_id: eventData.data.query_id,
            text: eventData.data.text,
            attempt: eventData.data.attempt || 1,
            timestamp: eventData.timestamp
          };
          // 重複チェック
          const existingQueryIndex = newState.queries.findIndex(q => q.query_id === query.query_id);
          if (existingQueryIndex >= 0) {
            newState.queries[existingQueryIndex] = query;
          } else {
            newState.queries.push(query);
          }
          break;

        case 'search_result':
          const result: SearchResult = {
            query_id: eventData.data.query_id,
            rank: eventData.data.rank,
            url: eventData.data.url,
            title: eventData.data.title,
            snippet: eventData.data.snippet,
            timestamp: eventData.timestamp
          };
          // 重複チェック
          const existingResultIndex = newState.results.findIndex(
            r => r.query_id === result.query_id && r.rank === result.rank
          );
          if (existingResultIndex >= 0) {
            newState.results[existingResultIndex] = result;
          } else {
            newState.results.push(result);
          }
          break;

        case 'search_complete':
          newState.status = 'completed';
          newState.totalResults = eventData.data.total_results;
          newState.duration = eventData.data.duration_ms;
          if (eventData.data.error) {
            newState.status = 'error';
            newState.error = eventData.data.error;
          }
          // 5秒後に自動的に非アクティブ状態にする
          setTimeout(() => {
            setSearchProgress(prev => ({
              ...prev,
              isActive: false
            }));
          }, 5000);
          break;

        case 'search_cancelled':
          newState.status = 'cancelled';
          newState.error = `検索がキャンセルされました: ${eventData.data.reason || '不明な理由'}`;
          break;

        case 'search_error':
          newState.status = 'error';
          newState.error = eventData.data.error;
          break;

        default:
          console.warn('SearchProgress: 不明なイベントタイプ', eventData.event);
      }

      return newState;
    });
  }, [messageId]);

  const resetProgress = useCallback(() => {
    setSearchProgress(initialState);
  }, []);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttempts.current = 0;
  }, []);

  // chatIdまたはmessageIdが変更されたときに再接続
  useEffect(() => {
    console.log('useSearchProgress: useEffect triggered', { chatId, messageId });
    if (chatId) {
      console.log('useSearchProgress: Calling connectEventSource');
      connectEventSource();
    } else {
      console.log('useSearchProgress: No chatId, calling disconnect');
      disconnect();
    }

    return disconnect;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, messageId]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return disconnect;
  }, [disconnect]);

  return {
    searchProgress,
    resetProgress,
    disconnect
  };
};